import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, Hash, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { useVenue } from '../../contexts/VenueContext';
import { fetchSelectedVenueApplication, saveSelectedVenueApplication } from '../../utils/venueAppApi';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import { VenueSetupBackButton } from '../../components/venue/VenueSetupBackButton';
import { VenueCardPreview } from '../../components/venue/VenueCardPreview';
import { SettingStatusPill } from '../../components/venue/SettingStatusPill';
import {
  VENUE_CATEGORIES,
  VENUE_CATEGORIES_MAX,
  VENUE_CATEGORIES_MIN,
  migrateVenueCategories,
} from '../../utils/venueCategories';
import { presetTagsForCategories, normalizeTag } from '../../utils/venueTags';

const FONT = "'Inria Serif', serif";
const SUBTITLE_COLOR = 'var(--text-3)';
const DESCRIPTION_MAX_LENGTH = 60;

const chipBaseStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '11px 15px',
  borderRadius: 14,
  fontFamily: FONT,
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
};

function chipStyle(isActive, isDisabled) {
  return {
    ...chipBaseStyle,
    border: isActive ? '1.5px solid var(--accent-purple)' : '1.5px solid var(--card-border)',
    background: isActive ? 'var(--accent-purple)' : 'var(--surface-2)',
    color: isActive ? '#FFFFFF' : 'var(--text)',
    boxShadow: isActive ? 'var(--premium-shadow-main)' : 'none',
    opacity: !isActive && isDisabled ? 0.45 : 1,
    cursor: !isActive && isDisabled ? 'not-allowed' : 'pointer',
  };
}

function tagChipStyle(isActive) {
  return {
    ...chipBaseStyle,
    padding: '8px 12px',
    fontSize: 13,
    borderRadius: 11,
    border: isActive ? '1.5px solid var(--accent-purple)' : '1.5px solid var(--card-border)',
    background: isActive ? 'var(--accent-purple)' : 'var(--surface-2)',
    color: isActive ? '#FFFFFF' : 'var(--text)',
    boxShadow: isActive ? 'var(--premium-shadow-main)' : 'none',
  };
}

const inputShell = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  background: 'var(--surface)',
  border: '1.5px solid var(--card-border)',
  borderRadius: 16,
  padding: '12px 14px',
  boxShadow: 'var(--premium-shadow-nested)',
};

const inputControl = {
  flex: 1,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontFamily: FONT,
  fontSize: 14.5,
  color: 'var(--text)',
  minWidth: 0,
};

const sectionLabel = {
  margin: '0 0 10px 4px',
  fontFamily: FONT,
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--accent-purple)',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

/**
 * Lets a venue owner edit the categories that drive the subtitle guests
 * see on the map card (e.g. "Mediterranean · Fine Dining"), plus the
 * search-only tags that help guests find dishes in search. A live
 * preview at the top mirrors the exact compact guest-map card so the
 * owner always sees what they're changing.
 *
 * Categories are a fixed 1–3 selection from the canonical taxonomy —
 * no free-text entry (removed per PM decision to keep the guest map's
 * chip filter honest). Legacy custom / free-typed categories are
 * silently migrated on load.
 */
export default function VenueCardSettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeVenueAppId } = useVenue();
  const [application, setApplication] = useState(null);
  const [selected, setSelected] = useState([]);
  const [tags, setTags] = useState([]);
  const [description, setDescription] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const saveTimer = useRef(null);
  const latestRef = useRef({ selected: [], tags: [], description: '' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const app = await fetchSelectedVenueApplication(activeVenueAppId);
        if (cancelled) return;
        setApplication(app);
        // Silent migration from the legacy 8-slug taxonomy + any
        // free-typed custom categories the venue had stored. Anything
        // that can't be remapped to a canonical slug is dropped.
        setSelected(migrateVenueCategories(app?.venue_categories));
        setTags(Array.isArray(app?.venue_tags) ? app.venue_tags : []);
        setDescription(app?.map_description || '');
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) { setLoading(false); setLoaded(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [activeVenueAppId]);

  // Auto-save whenever categories / tags / description change (debounced
  // 500ms). Skips the first render after load so we don't POST unchanged
  // data. Failures surface a toast; the input never regresses in state.
  useEffect(() => {
    latestRef.current = { selected, tags, description };
    if (!loaded) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await saveSelectedVenueApplication(
          {
            venue_categories: latestRef.current.selected,
            venue_tags: latestRef.current.tags,
            map_description: (latestRef.current.description || '').trim(),
            step: 4,
            submitted: false,
          },
          activeVenueAppId,
        );
      } catch (err) {
        toast.error('Couldn’t save — check your connection');
      }
    }, 500);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [selected, tags, description, loaded, activeVenueAppId]);

  // Flush any pending debounced save immediately. Used by the back
  // button so edits made in the last 500ms aren't dropped when the
  // user leaves the page.
  const flushPendingSave = async () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
      try {
        await saveSelectedVenueApplication(
          {
            venue_categories: latestRef.current.selected,
            venue_tags: latestRef.current.tags,
            map_description: (latestRef.current.description || '').trim(),
            step: 4,
            submitted: false,
          },
          activeVenueAppId,
        );
      } catch { /* silent — user is leaving */ }
    }
  };

  const capReached = selected.length >= VENUE_CATEGORIES_MAX;

  const toggleCategory = (slug) => {
    setSelected((current) => {
      if (current.includes(slug)) {
        return current.filter((s) => s !== slug);
      }
      if (current.length >= VENUE_CATEGORIES_MAX) {
        toast(`Pick up to ${VENUE_CATEGORIES_MAX} categories. Remove one first.`, {
          id: 'venue-category-cap',
          duration: 2600,
        });
        return current;
      }
      return [...current, slug];
    });
  };

  const presetSlugs = useMemo(() => (
    selected.filter((value) => VENUE_CATEGORIES.some((cat) => cat.slug === value))
  ), [selected]);
  const availablePresetTags = useMemo(() => presetTagsForCategories(presetSlugs), [presetSlugs]);

  const toggleTag = (tag) => {
    const normalized = normalizeTag(tag);
    if (!normalized) return;
    setTags((current) => (
      current.includes(normalized) ? current.filter((t) => t !== normalized) : [...current, normalized]
    ));
  };

  const handleAddCustomTag = (raw) => {
    const normalized = normalizeTag(raw);
    if (!normalized) return;
    if (!tags.includes(normalized)) setTags((current) => [...current, normalized]);
    setTagInput('');
  };

  const handleTagInputKeyDown = (event) => {
    if (event.key === 'Enter' && tagInput.trim()) {
      event.preventDefault();
      handleAddCustomTag(tagInput);
    }
  };

  const customTags = useMemo(() => (
    tags.filter((tag) => !availablePresetTags.includes(tag))
  ), [tags, availablePresetTags]);

  const helperText = useMemo(() => {
    if (selected.length === 0) {
      return `Pick ${VENUE_CATEGORIES_MIN}–${VENUE_CATEGORIES_MAX} categories that fit your venue.`;
    }
    const subtitle = selected
      .map((slug) => VENUE_CATEGORIES.find((c) => c.slug === slug)?.display || slug)
      .join(' · ');
    return `Subtitle preview: ${subtitle}`;
  }, [selected]);

  if (loading) {
    return (
      <div className="app-shell" style={{ minHeight: '100vh', background: 'var(--app-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: FONT, color: 'var(--text-3)' }}>Loading…</div>
      </div>
    );
  }

  return (
    <div
      className="app-shell"
      data-testid="venue-card-settings-page"
      style={{ minHeight: '100vh', backgroundColor: 'var(--app-bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 40 }}
    >
      <div style={{ width: '100%', maxWidth: 390, display: 'flex', justifyContent: 'flex-start', padding: '16px 20px 0' }}>
        <VenueSetupBackButton testId="venue-card-settings-back" onBeforeNavigate={flushPendingSave} />
      </div>
      <div style={{ marginTop: 8 }}>
        <RouteTransitionLogo width={100} height={89} dataTestId="venue-card-settings-shared-logo" />
      </div>
      <h2 style={{ fontFamily: FONT, fontSize: 26, fontWeight: 700, color: 'var(--text)', marginTop: 8, marginBottom: 4 }}>
        Venue Card
      </h2>
      <p style={{ fontFamily: FONT, fontSize: 14, color: 'var(--text-2)', textAlign: 'center', margin: '0 24px 20px', lineHeight: 1.4 }}>
        This is exactly what guests see on the map. Update your categories, write a short description, and manage your search tags below.
      </p>

      {(() => {
        const missing = [];
        if (selected.length === 0) missing.push('categories');
        if (!description.trim()) missing.push('description');
        if (tags.length === 0) missing.push('a tag');
        const complete = missing.length === 0;
        const label = missing.length === 1
          ? missing[0]
          : missing.length === 2
            ? `${missing[0]} and ${missing[1]}`
            : `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}`;
        return (
          <div style={{ marginBottom: 18 }}>
            <SettingStatusPill
              complete={complete}
              missingText={`Missing ${label}`}
              testId="venue-card-settings-status-pill"
            />
          </div>
        );
      })()}

      <div style={{ width: '100%', maxWidth: 360, padding: '0 16px' }}>
        <VenueCardPreview
          testId="venue-card-settings-preview"
          photoUrl={Array.isArray(application?.photo_urls) ? application.photo_urls[0] : ''}
          name={application?.restaurant_name}
          categories={selected}
          description={description}
        />

        {/* Categories — drives the subtitle line on the card */}
        <div style={{ marginTop: 28 }}>
          <p style={sectionLabel}>Categories · sets your subtitle</p>

          <div data-testid="venue-card-settings-category-card" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: 4 }}>
            {VENUE_CATEGORIES.map((cat) => {
              const isActive = selected.includes(cat.slug);
              return (
                <button
                  key={cat.slug}
                  type="button"
                  onClick={() => toggleCategory(cat.slug)}
                  data-testid={`venue-card-settings-chip-${cat.slug}`}
                  aria-pressed={isActive}
                  aria-disabled={!isActive && capReached}
                  style={chipStyle(isActive, capReached)}
                >
                  {isActive ? <Check size={13} strokeWidth={3} /> : null}
                  {cat.display}
                </button>
              );
            })}
          </div>
          <p style={{ marginTop: 10, marginLeft: 4, fontFamily: FONT, fontSize: 12.5, fontWeight: 600, color: selected.length === 0 ? 'var(--text-3)' : 'var(--text)' }}>
            {helperText}
          </p>
        </div>

        {/* Card description — shown as its own line beneath the category subtitle */}
        <div style={{ marginTop: 26 }}>
          <p style={sectionLabel}>Description · shown beneath your category</p>
          <div style={inputShell}>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_MAX_LENGTH))}
              maxLength={DESCRIPTION_MAX_LENGTH}
              placeholder="e.g. Incredibly massive burgers!"
              data-testid="venue-card-settings-description-input"
              rows={2}
              autoCapitalize="sentences"
              autoCorrect="on"
              spellCheck
              style={{ ...inputControl, resize: 'none' }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, padding: '0 4px' }}>
            <p style={{ margin: 0, fontFamily: FONT, fontSize: 12.5, fontWeight: 600, color: description.trim() ? 'var(--text)' : SUBTITLE_COLOR }}>
              {description.trim() ? `Card preview: ${description.trim()}` : 'A short, punchy line guests see beneath your categories.'}
            </p>
            <span data-testid="venue-card-settings-description-counter" style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: SUBTITLE_COLOR, flexShrink: 0, marginLeft: 8 }}>
              {description.length}/{DESCRIPTION_MAX_LENGTH}
            </span>
          </div>
        </div>

        {/* Search tags — not shown on the card, help guests search dishes */}
        <div data-testid="venue-card-settings-tags-section" style={{ marginTop: 26 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ width: 28, height: 28, borderRadius: 9, background: 'var(--accent-surface-soft)', color: 'var(--accent-purple)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <Hash size={14} strokeWidth={2.4} />
            </span>
            <div>
              <p style={{ margin: 0, fontFamily: FONT, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Search tags</p>
              <p style={{ margin: '1px 0 0', fontFamily: FONT, fontSize: 12.5, fontWeight: 600, color: SUBTITLE_COLOR }}>
                Not shown on your card — helps guests find you when they search dishes.
              </p>
            </div>
          </div>

          <div style={{ ...inputShell, marginTop: 10 }}>
            <Hash size={15} color="#9088B0" strokeWidth={2.2} />
            <input
              type="text"
              placeholder="Add a tag (e.g. tiramisu, ramen, mojito)…"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagInputKeyDown}
              data-testid="venue-card-settings-tag-input"
              style={inputControl}
              autoCapitalize="none"
              autoCorrect="on"
              spellCheck
            />
            {tagInput.trim() ? (
              <button
                type="button"
                onClick={() => handleAddCustomTag(tagInput)}
                data-testid="venue-card-settings-tag-add-button"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 11px', borderRadius: 999, border: 'none', background: 'var(--accent-purple)', color: '#FFFFFF', fontFamily: FONT, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', boxShadow: 'var(--premium-shadow-main)' }}
              >
                <Plus size={13} strokeWidth={2.6} />
                Add
              </button>
            ) : null}
          </div>

          {availablePresetTags.length > 0 || customTags.length > 0 ? (
            <div data-testid="venue-card-settings-tag-chips" style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 7, padding: 4 }}>
              {availablePresetTags.map((tag) => {
                const isActive = tags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    data-testid={`venue-card-settings-tag-${tag.replace(/[^a-z0-9]+/g, '-')}`}
                    aria-pressed={isActive}
                    style={tagChipStyle(isActive)}
                  >
                    {isActive ? <Check size={11} strokeWidth={3} /> : null}
                    {tag}
                  </button>
                );
              })}
              {customTags.map((tag) => (
                <button
                  key={`custom-tag-${tag}`}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  data-testid={`venue-card-settings-tag-custom-${tag.replace(/[^a-z0-9]+/g, '-')}`}
                  aria-pressed
                  style={{ ...tagChipStyle(true), paddingRight: 10 }}
                >
                  <Check size={11} strokeWidth={3} />
                  {tag}
                  <X size={11} strokeWidth={2.6} style={{ opacity: 0.85, marginLeft: 2 }} />
                </button>
              ))}
            </div>
          ) : (
            <p data-testid="venue-card-settings-tag-empty" style={{ marginTop: 10, marginLeft: 4, fontFamily: FONT, fontSize: 12.5, color: 'var(--text-3)', fontWeight: 600 }}>
              Pick a category above for suggestions, or type any tag.
            </p>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={() => {}}
        disabled
        data-testid="venue-card-settings-save"
        style={{ display: 'none' }}
      >
        Saved
      </button>
    </div>
  );
}
